import React, { useState, useEffect } from 'react';
import CommonGrid from './CommonGrid';
import CommonHeader from './CommonHeader';
import API_BASE_URL from '../config/api';
import ReactDOM from 'react-dom';
import { useToast } from "../components/ToastContext";

const Documents = () => {
    const { showToast } = useToast();
    const [viewUrl, setViewUrl] = useState<string | null>(null);
    const [viewType, setViewType] = useState<string>('');
    const [documents, setDocuments] = useState<any[]>([]);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // Chat mock
    const [chatMessages, setChatMessages] = useState([
        { id: 1, sender: "client", message: "Hello, I need help with my visa." },
        { id: 2, sender: "operator", message: "Sure, please provide your details." },
    ]);
    const [newMessage, setNewMessage] = useState("");

    // Fetch documents from API
    useEffect(() => {
        if (user?.id) {
            fetch(`${API_BASE_URL}/documents/user/${user.id}`)
                .then(res => res.json())
                .then(data => {
                    setDocuments(data.documents || []);
                });
        }
    }, [user?.id]);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [description, setDescription] = useState('');
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [uploadStatus, setUploadStatus] = useState<string>('');

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.size > 50 * 1024 * 1024) { // 50MB limit
                showToast("File size exceeds 50MB limit.", "error");
                setSelectedFile(null);
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        if (selectedFile) {
            setUploadStatus('Uploading...');
            setUploadProgress(0);
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('userId', user.id);
            formData.append('description', description);

            // Use XMLHttpRequest for progress
            const xhr = new XMLHttpRequest();
            xhr.open('POST', `${API_BASE_URL}/documents/upload`, true);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const percent = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(percent);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200) {
                    try {
                        const data = JSON.parse(xhr.responseText);
                        if (data.document) {
                            setDocuments(prev => [...prev, data.document]);
                            setSelectedFile(null);
                            setDescription('');
                            setUploadStatus('Upload successful!');
                            showToast("Document uploaded successfully!", "success");
                        } else {
                            setUploadStatus('Failed to upload document.');
                            showToast("Failed to upload document.", "error");
                        }
                    } catch {
                        setUploadStatus('Failed to upload document.');
                        showToast("Failed to upload document.", "error");
                    }
                } else {
                    setUploadStatus('Failed to upload document.');
                    showToast("Failed to upload document.", "error");
                }
                setUploadProgress(0);
            };

            xhr.onerror = () => {
                setUploadStatus('Upload error.');
                setUploadProgress(0);
                showToast("Upload error.", "error");
            };

            xhr.send(formData);
        }
    };

    // Document delete handler (API)
    const handleDelete = async (id: number) => {
        const res = await fetch(`${API_BASE_URL}/documents/delete/${id}`, {
            method: 'DELETE'
        });
        const data = await res.json();
        if (data.success) {
            setDocuments(prev => prev.filter(doc => doc.id !== id));
            showToast("Document deleted successfully!", "success");
        } else {
            showToast(data.message || 'Failed to delete document', "error");
        }
    };

    // Document download handler
    const handleDownload = (fileName: string) => {
        window.open(`${API_BASE_URL}/documents/download/${fileName}`, '_blank');
        showToast("Document download started.", "info");
        };

        // Document view handler
        const handleView = (fileName: string) => {
            const url = `${API_BASE_URL}/documents/download/${fileName}`;
            setViewUrl(url);
            // Detect file type by extension
            const ext = fileName.split('.').pop()?.toLowerCase();
            if (['pdf', 'jpg', 'jpeg', 'png', 'gif'].includes(ext || '')) {
                setViewType(ext || '');
            } else {
                setViewType('other');
            }
        };

        const closeModal = () => {
            setViewUrl(null);
            setViewType('');
    };

    // Chat send handler (mock)
    const handleSendMessage = () => {
        if (newMessage.trim()) {
            setChatMessages([...chatMessages, { id: Date.now(), sender: "client", message: newMessage }]);
            setNewMessage("");
            // TODO: Send mail to operator/admin here
        }
    };

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteDocId, setDeleteDocId] = useState<number | null>(null);

    return (
        <div className="flex h-[calc(100vh-67px)]">
            <div className="flex-1">
                <CommonHeader title="Documents" />
                <main className="flex-1 p-4 sm:p-8 lg:p-10">
                    {/* Document Section */}
                    <section className="mb-8">
                        <div className="max-w-xl mx-auto bg-white border border-gray-200 rounded-lg shadow p-6 mb-6">
                            <h3 className="text-lg font-bold mb-4 text-gray-800">Upload New Document</h3>
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-4">
                                    <label className="w-32 text-sm font-medium text-gray-700 mb-0 text-left">Select Document</label>
                                    <input type="file" onChange={handleFileChange} className="block flex-1 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div className="flex items-center gap-4">
                                    <label className="w-32 text-sm font-medium text-gray-700 mb-0 text-left">Description</label>
                                    <input
                                        type="text"
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        className="block flex-1 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter a short description"
                                    />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <div className="flex justify-end">
                                        <button
                                            className={`bg-blue-600 text-white px-4 py-2 rounded font-semibold transition-colors ${selectedFile ? 'hover:bg-blue-700' : 'opacity-50 cursor-not-allowed'}`}
                                            onClick={handleUpload}
                                            disabled={!selectedFile}
                                        >
                                            Upload
                                        </button>
                                    </div>
                                    {uploadStatus && (
                                        <div className="text-sm text-gray-700 mt-2">{uploadStatus}</div>
                                    )}
                                    {uploadProgress > 0 && (
                                        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                                            <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                                        </div>
                                        )}
                                </div>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow">
                                <thead>
                                    <tr className="bg-gray-100 text-gray-700">
                                        <th className="px-4 py-2 border">Sr. No</th>
                                        <th className="px-4 py-2 border">Document Name</th>
                                        <th className="px-4 py-2 border">Description</th>
                                        <th className="px-4 py-2 border">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {documents.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-center py-4 text-gray-500">No documents found.</td>
                                        </tr>
                                    ) : (
                                        documents.map((doc, idx) => (
                                            <tr key={doc.id} className="border-b hover:bg-gray-50">
                                                <td className="px-4 py-2 border text-center">{idx + 1}</td>
                                                <td className="px-4 py-2 border font-semibold">{doc.file_name}</td>
                                                <td className="px-4 py-2 border text-gray-600">{doc.description}</td>
                                                <td className="px-4 py-2 border text-center flex flex-col sm:flex-row gap-2 justify-center items-center">
                                                    <button className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600" onClick={() => handleView(doc.file_path)}>View</button>
                                                    <button className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600" onClick={() => handleDownload(doc.file_path)}>Download</button>
                                                    <button
                                                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                                                      onClick={() => {
                                                        setDeleteDocId(doc.id);
                                                        setShowDeleteConfirm(true);
                                                      }}
                                                    >
                                                      Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                    {/* Chat Section */}
                    {/* <section>
                        <h2 className="text-xl font-bold mb-4">Chat</h2>
                        <div className="border rounded p-4 mb-4 h-64 overflow-y-auto bg-white">
                            {chatMessages.map(msg => (
                                <div key={msg.id} className={`mb-2 text-left ${msg.sender === "client" ? "text-blue-700" : "text-green-700"}`}>
                                    <span className="font-bold">{msg.sender === "client" ? "You" : "Operator/Admin"}:</span> {msg.message}
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newMessage}
                                onChange={e => setNewMessage(e.target.value)}
                                className="border rounded p-2 flex-1"
                                placeholder="Type your message..."
                            />
                            <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={handleSendMessage}>Send</button>
                        </div>
                    </section> */}
                </main>
                {/* Document View Modal */}
                {viewUrl && (
                    ReactDOM.createPortal(
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                            <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-4 relative">
                                <button className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 text-xl font-bold" onClick={closeModal}>&times;</button>
                                <h3 className="text-lg font-bold mb-4">Document Preview</h3>
                                {viewType === 'pdf' ? (
                                    <iframe src={viewUrl} title="Document" className="w-full h-96 border rounded" />
                                ) : viewType === 'jpg' || viewType === 'jpeg' || viewType === 'png' || viewType === 'gif' ? (
                                    <img src={viewUrl} alt="Document" className="max-w-full max-h-96 mx-auto" />
                                ) : (
                                    <div className="text-center">
                                        <p>Preview not available for this file type.</p>
                                        <a href={viewUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Download &amp; Open</a>
                                    </div>
                                )}
                            </div>
                        </div>,
                        document.body
                    )
                )}
                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                  ReactDOM.createPortal(
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                      <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6 relative">
                        <button
                          className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 text-xl font-bold"
                          onClick={() => setShowDeleteConfirm(false)}
                        >
                          &times;
                        </button>
                        <h3 className="text-lg font-bold mb-4 text-center">Confirm Delete</h3>
                        <p className="mb-6 text-center">Are you sure you want to delete this document?</p>
                        <div className="flex justify-center gap-4">
                          <button
                            className="bg-red-600 text-white px-4 py-2 rounded font-semibold"
                            onClick={async () => {
                              if (deleteDocId !== null) {
                                await handleDelete(deleteDocId);
                                setShowDeleteConfirm(false);
                                setDeleteDocId(null);
                              }
                            }}
                          >
                            Yes, Delete
                          </button>
                          <button
                            className="bg-gray-300 text-gray-800 px-4 py-2 rounded font-semibold"
                            onClick={() => setShowDeleteConfirm(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>,
                    document.body
                  )
                )}
            </div>
        </div>
    );
};

export default Documents;
